import { isPlatformBrowser, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  OnDestroy,
  OnInit,
  ContentChild,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { IconCheckComponent, IconCloseComponent } from '@pedix-workspace/angular-ui-icons';
import slugify from 'slugify';
import clsx from 'clsx';
import { ModalHistoryComponent } from '../modal-history/modal-history.component';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  CardFooterComponent,
  CardHeaderComponent,
  CardComponent,
} from '@pedix-workspace/angular-ui-blocks';
import { ModalZIndexService } from '../modal-z-index.service';

export const modalFormStyles = [
  'small',
  'normal',
  'medium',
  'large',
  'wide',
  'embeeded',
  'fullscreen',
] as const;

export type ModalFormStyles = typeof modalFormStyles;
export type ModalFormStyle = ModalFormStyles[number];

export type CloseReason = 'close' | 'dismiss' | 'backbutton';

@Component({
  selector: 'pxw-modal-form',
  templateUrl: './modal-form.component.html',
  styleUrls: ['./modal-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CardHeaderComponent,
    NgTemplateOutlet,
    IconCloseComponent,
    IconCheckComponent,
    CardFooterComponent,
    ButtonComponent,
    ModalHistoryComponent,
    NgStyle,
  ],
})
export class ModalFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private modalService = inject(NgbModal);
  private modalZIndexService = inject(ModalZIndexService);

  @ViewChild('modalContentWrapper', { read: TemplateRef })
  modalContentWrapper!: TemplateRef<unknown>;
  @ViewChild(ModalHistoryComponent) modalHistoryComponent: ModalHistoryComponent;

  @ContentChild('modalTitle') modalTitle: TemplateRef<HTMLElement>;
  @ContentChild('modalContent') modalContent: TemplateRef<HTMLElement>;
  @ContentChild('modalFooter') modalFooter: TemplateRef<{
    close: ModalFormComponent['close'];
  }> | null = null;

  @Output() modalAction = new EventEmitter<NgbModalRef>();
  @Output() modalClose = new EventEmitter<CloseReason>();
  @Output() modalOpen = new EventEmitter<boolean>();

  @Input() openOnInit = false;
  @Input() loading = false;
  @Input() title = '';
  @Input() buttonText = '';
  @Input() buttonDisabled: boolean;
  @Input() modalStyle: ModalFormStyle = 'normal';
  @Input() isBoundToRoute = false;
  @Input() hideCloseButton = false;
  @Input() disablePadding = false;
  @Input() modalClasses: string;
  @Input() contentScrollEnabled = true;
  @Input() autoHeight = false;
  @Input() bodyStyles: Record<string, string> = {};
  /**
   * Hook para cierres iniciados por el usuario (X / botón atrás). Si devuelve `false` (o
   * `Promise<false>`) se cancela el cierre. Por defecto (sin definir) el modal cierra normalmente.
   * NO afecta a `close()` programático.
   */
  @Input() beforeCloseFn?: () => boolean | Promise<boolean>;

  @HostBinding('class') get classNames() {
    return clsx(this.modalStyle, {
      'with-footer': this.buttonText,
    });
  }

  modalRef!: NgbModalRef;
  closeMethod: CloseReason | undefined;
  modalFormName = 'modal-form';

  #isOpen = signal(false);

  get isOpen() {
    return this.#isOpen();
  }

  ngOnInit() {
    this.modalFormName = slugify(this.title);
  }

  ngAfterViewInit() {
    if (this.openOnInit && isPlatformBrowser(this.platformId)) {
      this.open();
    }
  }

  ngOnDestroy() {
    if (!this.isOpen) {
      return;
    }
    this.toggleOpen(false);

    if (this.closeMethod === 'close') {
      this.modalRef?.close();
    } else {
      // If the browser's back button is pressed, then we consider this action to be a "dismiss"
      // As this wasn't triggered by the UI, we need to emit the event in case some other component is interested on it
      if (!this.closeMethod) {
        this.modalClose.emit('dismiss');
      }
      this.modalRef?.dismiss();
    }
  }

  /** Cierre iniciado por el usuario (X / atrás): consulta `beforeCloseFn` y solo cierra si no lo veta. */
  requestClose = async (reason: CloseReason = 'close') => {
    if (this.beforeCloseFn) {
      const canClose = await this.beforeCloseFn();

      if (canClose === false) {
        return;
      }
    }

    await this.close(reason);
  };

  // This is a bound method
  close = (reason: CloseReason = 'close') => {
    if (!this.isOpen) {
      return Promise.resolve();
    }

    return new Promise<void>(resolve => {
      // NOTE: for some reason, this should be called asynchronously to prevent the modal opening again
      setTimeout(() => {
        this.closeMethod = reason;
        this.modalClose.emit(this.closeMethod);

        resolve();
      }, 50);

      if (!this.isBoundToRoute) {
        this.modalRef.dismiss();
        this.toggleOpen(false);
      }

      setTimeout(() => {
        this.closeMethod = undefined;
      }, 100);
    });
  };

  action() {
    this.modalAction.emit(this.modalRef);
  }

  open() {
    if (this.isOpen) {
      return;
    }

    this.modalRef = this.modalService.open(this.modalContentWrapper, {
      windowClass: `animated bounceInUp pxw-modal-form ${this.modalStyle} ${this.modalClasses} ${this.autoHeight ? 'pxw-modal-form--auto-height' : ''}`,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    // Register modal for z-index management
    this.modalZIndexService.registerModal(this.modalRef);
    this.toggleOpen(true);
  }

  private toggleOpen(isOpen: boolean) {
    this.#isOpen.set(isOpen);

    this.modalOpen.emit(this.isOpen);
  }
}
